import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { NAV_GROUPS, NAV_KEYWORDS } from "@/lib/nav";
import { cn } from "@/lib/utils";

export const RESULTS_ID = "sidebar-search-results";
export const optionId = (item) => `sidebar-search-opt-${item.to.replace(/[^a-z0-9]+/gi, "-")}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const haystack = (item) => {
  const g = NAV_GROUPS.find((x) => x.key === item.group);
  return `${item.label} ${g?.label || ""} ${NAV_KEYWORDS[item.to] || ""} ${item.to.replace(/[/-]/g, " ")}`.toLowerCase();
};

// Returns matches grouped in sidebar order: [{ group, items }]
export function searchNav(items, query) {
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const hits = items.filter((i) => terms.every((t) => haystack(i).includes(t)));
  const rank = (i) => (i.label.toLowerCase().startsWith(terms[0]) ? 0 : i.label.toLowerCase().includes(terms[0]) ? 1 : 2);
  return NAV_GROUPS.map((group) => ({
    group,
    items: hits.filter((i) => i.group === group.key).sort((a, b) => rank(a) - rank(b)),
  })).filter((g) => g.items.length);
}

export function SidebarSearchInput({ query, onChange, onKeyDown, activeItem, hasResults }) {
  return (
    <div className="relative px-3 pt-3">
      <Search className="pointer-events-none absolute left-6 top-1/2 mt-1.5 h-4 w-4 -translate-y-1/2 text-pawkt-text-disabled" aria-hidden="true" />
      <input
        type="text"
        role="combobox"
        aria-label="Search menu"
        aria-autocomplete="list"
        aria-expanded={!!query && hasResults}
        aria-controls={RESULTS_ID}
        aria-activedescendant={activeItem ? optionId(activeItem) : undefined}
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search menu…"
        data-testid="sidebar-search-input"
        className="h-9 w-full rounded-xl border border-pawkt-border bg-pawkt-elevated pl-9 pr-8 text-sm text-pawkt-text placeholder:text-pawkt-text-disabled transition-colors focus:border-primary focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      />
      {query && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear menu search"
          data-testid="sidebar-search-clear"
          className="absolute right-5 top-1/2 mt-1.5 -translate-y-1/2 rounded-md p-0.5 text-pawkt-text-secondary hover:text-pawkt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function SidebarSearchResults({ query, groups, activeItem, onHover, onSelect }) {
  if (!groups.length) {
    return (
      <div role="status" data-testid="sidebar-search-empty" className="px-3 py-6 text-center text-sm text-pawkt-text-secondary">
        No pages match “{query.trim()}”.
        <div className="mt-1 text-xs text-pawkt-text-disabled">Try another name, like “invoice” or “stock”.</div>
      </div>
    );
  }
  return (
    <div id={RESULTS_ID} role="listbox" aria-label="Menu search results" data-testid="sidebar-search-results" className="space-y-2">
      {groups.map(({ group, items }) => (
        <div key={group.key} role="group" aria-labelledby={`sidebar-search-group-${group.key}`}>
          <div id={`sidebar-search-group-${group.key}`} className="px-3 pb-1 pt-1 text-[11px] font-bold uppercase tracking-wide text-pawkt-text-disabled">
            {group.label}
          </div>
          {items.map((item) => {
            const sel = activeItem?.to === item.to;
            return (
              <Link
                key={item.to}
                id={optionId(item)}
                to={item.to}
                role="option"
                aria-selected={sel}
                tabIndex={-1}
                onMouseEnter={() => onHover(item)}
                onClick={() => onSelect(item, false)}
                data-testid={`nav-search-result-${slug(item.label)}`}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors duration-150",
                  sel ? "bg-pawkt-primary-soft text-pawkt-primary-deep" : "text-pawkt-text-secondary hover:bg-pawkt-elevated"
                )}
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}
